/**
 * The flooring-type pick list, shared by the free-element inspector and the
 * flooring-by-type rows. What it shows comes from editor-core's
 * floorTypeChoices over the editor's floorTypeOptions (host-overridable via
 * the FloorPlanEditor `floorTypes` prop): the option KEY is what gets stored,
 * the LABEL is what the user sees, and undefined is unset.
 */

import { floorTypeChoices, floorTypeLabel, pickedFloorType, useFloorPlanEditorState } from '@kaiizen/kai-floor-plan-editor-core';
import { OptionMenuSelect } from '../OptionMenu';
import { FIELD_ROW_STYLE } from './chrome';
import { ReadOnlyField } from './fields';
import { usePanelReadOnly } from './panelReadOnly';

/** THE flooring-type pick list: the OptionMenu dropdown over the floor type choices. */
export function FloorTypePicker({ id, ariaLabel, value, onCommit }: {
  /** Stable E2E hook on the trigger button. */
  id?: string;
  ariaLabel?: string;
  /** The stored floor-type KEY currently in effect; undefined or empty = unset. */
  value: string | undefined;
  /** Receives the picked option's KEY, or undefined for the unpick entry. */
  onCommit: (key: string | undefined) => void;
}): React.JSX.Element {
  const { st } = useFloorPlanEditorState();
  const { items, selectedKey, placeholder, enabled } = floorTypeChoices(st.floorTypeOptions, value);
  return (
    <OptionMenuSelect
      id={id}
      ariaLabel={ariaLabel}
      value={selectedKey}
      placeholder={placeholder}
      disabled={!enabled}
      options={items.map(item => [item.key, item.label])}
      onSelect={key => onCommit(pickedFloorType(key))}
    />
  );
}

/** The label a flooring type shows as in a read-only panel (core's `floorTypeLabel`). */
export function useFloorTypeLabel(value: string | undefined): string {
  return floorTypeLabel(useFloorPlanEditorState().st.floorTypeOptions, value);
}

/** A labeled field row hosting the FloorTypePicker (the inspectors' layout). */
export function FloorTypeSelect({ id, label, value, onCommit }: {
  id: string;
  label: string;
  /** The stored floor-type KEY currently on the element; undefined or empty = unset. */
  value: string | undefined;
  /** Receives the picked option's KEY, or undefined for the unpick entry. */
  onCommit: (key: string | undefined) => void;
}): React.JSX.Element {
  const floorTypeLabel = useFloorTypeLabel(value);
  if (usePanelReadOnly()) return <ReadOnlyField id={id} label={label} value={floorTypeLabel} />;
  return (
    <div css={FIELD_ROW_STYLE}>
      <label>{label}</label>
      <FloorTypePicker id={id} value={value} onCommit={onCommit} />
    </div>
  );
}
